import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../../context/Language/LanguageContext";
import EventItem from "./EventItem";

function EventsList({
  matches,
  team,
  filters,
  filterFn,
  selectedSet,
  selectedMatchGuid,
  doSelectEvent,
  onFilter,
  collapseAll,
  // Optional externally-controlled selection (e.g. Previous/Replay/Next Clip
  // toolbar buttons) -- takes priority over the internal click-driven state
  // below so the highlighted row stays in sync regardless of how the
  // selection changed, and its row is scrolled into view (see the ref/effect
  // near the EventItem render below).
  selectedEvent: selectedEventProp,
}) {
  const { t } = useLanguage();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const activeEvent = selectedEventProp ?? selectedEvent;
  const activeRowRef = useRef(null);
  const [selectedPlayerNames, setSelectedPlayerNames] = useState(null);
  const [selectedEventTypes, setSelectedEventTypes] = useState(null);
  const [selectedEventResults, setSelectedEventResults] = useState(null);
  const [selectedJumpHeights, setSelectedJumpHeights] = useState(null);
  const [collapseData, setCollapseData] = useState(null);
  const [collapsedSets] = useState(new Set());
  const [, forceUpdate] = useState(0);

  const onEventSelected = (ev) => {
    setSelectedEvent(ev);
    doSelectEvent(ev);
  };

  const doFilters = () => {
    var plnames = [];
    if (
      filters.teamAPlayers.length === 1 &&
      filters.teamAPlayers[0].value === 0
    ) {
      for (var match of matches) {
        const tm = team === match.teamA.Name ? match.teamA : match.teamB;
        for (var pl of tm.players) {
          const plname = pl.shirtNumber + ". " + pl.FirstName + " " + pl.LastName.toUpperCase();
          if (plnames.includes(plname) === false) {
            plnames.push(plname);
          }
        }
      }
    } else {
      for (var nta = 0; nta < filters.teamAPlayers.length; nta++) {
        plnames.push(filters.teamAPlayers[nta].guid);
      }
    }
    if (
      filters.teamBPlayers.length === 1 &&
      filters.teamBPlayers[0].value === 0
    ) {
      for (var match of matches) {
        const tm = team === match.teamA.Name ? match.teamB : match.teamA;
        for (var pl of tm.players) {
          const plname = pl.shirtNumber + ". " + pl.FirstName + " " + pl.LastName.toUpperCase();
          if (plnames.includes(plname) === false) {
            plnames.push(plname);
          }
        }
      }
    } else {
      for (var nta = 0; nta < filters.teamBPlayers.length; nta++) {
        plnames.push(filters.teamBPlayers[nta].guid);
      }
    }
    setSelectedPlayerNames(plnames);

    var games = [];
    if (filters.games.length === 1 && filters.games[0].value === 0) {
      for (var match of matches) {
        for (var ng = 1; ng <= match.sets.length; ng++) {
          if (games.includes(ng) === false) {
            games.push(ng);
          }
        }
      }
    } else {
      for (var nta = 0; nta < filters.games.length; nta++) {
        games.push(filters.games[nta].value);
      }
    }

    var rotations = [];
    if (filters.rotations.length === 1 && filters.rotations[0].value === 0) {
      for (var nr = 1; nr <= 6; nr++) {
        rotations.push(nr);
      }
    } else {
      for (var nta = 0; nta < filters.rotations.length; nta++) {
        rotations.push(filters.rotations[nta].value);
      }
    }

    var eventtypes = [];
    if (filters.eventTypes.length === 1 && filters.eventTypes[0].value === 0) {
      for (var nta = 1; nta <= 6; nta++) {
        eventtypes.push(nta);
      }
      eventtypes.push(20);
    } else if (
      filters.eventTypes.length === 1 &&
      filters.eventTypes[0].value === 1000
    ) {
      eventtypes.push(1);
      eventtypes.push(3);
      eventtypes.push(4);
      eventtypes.push(5);
      eventtypes.push(20);
    } else {
      for (var nta = 0; nta < filters.eventTypes.length; nta++) {
        eventtypes.push(filters.eventTypes[nta].value);
      }
    }

    var eventresults = [];
    if (
      filters.eventResults.length === 1 &&
      filters.eventResults[0].value === 0
    ) {
      eventresults.push("=");
      eventresults.push("/");
      eventresults.push("-");
      eventresults.push("!");
      eventresults.push("+");
      eventresults.push("#");
    } else {
      for (var nta = 0; nta < filters.eventResults.length; nta++) {
        eventresults.push(filters.eventResults[nta].label);
      }
    }

    var hittypes = [];
    for (var nta = 0; nta < filters.hitTypes.length; nta++) {
      hittypes.push(filters.hitTypes[nta].value);
    }

    var attacktypes = [];
    for (var nta = 0; nta < filters.attackTypes.length; nta++) {
      attacktypes.push(filters.attackTypes[nta].value);
    }

    var blocktypes = [];
    for (var nta = 0; nta < filters.blockTypes.length; nta++) {
      blocktypes.push(filters.blockTypes[nta].value);
    }

    var attackcombos = [];
    for (var nta = 0; nta < filters.attackCombos.length; nta++) {
      attackcombos.push(filters.attackCombos[nta].label);
    }

    var settercalls = [];
    for (var nta = 0; nta < filters.setterCalls.length; nta++) {
      settercalls.push(filters.setterCalls[nta].label);
    }

    // Power Play filter: value 0 = All, 1 = Power Play only, 2 = No Power
    // Play. Default to "All" (no-op) when the filter isn't set (e.g. an
    // older saved filter state from before this option existed).
    const powerPlaySel = filters.powerPlay ?? [{ value: 0 }];
    const powerPlayValues = powerPlaySel.map((p) => p.value);
    const showAllPowerPlay = powerPlayValues.length === 0 || powerPlayValues.includes(0);

    var mes = [];
    for (var match of matches) {
      for (var ns = 0; ns < match.sets.length; ns++) {
        var fes = [];
        var s = match.sets[ns];
        for (var ne = 0; ne < s.events.length; ne++) {
          const e = s.events[ne];
          if (games.includes(e.Drill.GameNumber) === false) continue;
          if (rotations.includes(e.Row) === false) continue;
          if (
            !showAllPowerPlay &&
            !(
              (powerPlayValues.includes(1) && e.powerPlay) ||
              (powerPlayValues.includes(2) && !e.powerPlay)
            )
          )
            continue;
          if (
            e.Player &&
            plnames.includes(
              e.Player.shirtNumber + ". " + e.Player.FirstName + " " + e.Player.LastName.toUpperCase()
            ) === false
          )
            continue;
          if (eventtypes.includes(e.EventType) === false) continue;
          // DVGrade ("=","/","-","!","+","#") is a DataVolley(DVW)-only field —
          // PSVB/VBStats events never set it, so it stays undefined. Without
          // this guard, `eventresults.includes(undefined)` is always false
          // (even with the default "All Results" filter, whose 6 chars never
          // include `undefined`), so EVERY VBStats event was unconditionally
          // excluded regardless of which filter was selected. Only apply this
          // filter dimension to events that actually carry a DVGrade.
          if (e.DVGrade !== undefined && eventresults.includes(e.DVGrade) === false) continue;
          if (eventtypes.includes(20) && e.EventType === 20) {
            if (
              settercalls.includes((e.settersCall ?? "").substring(0, 2)) === false &&
              settercalls.includes("All Calls") === false
            ) {
              continue;
            }
          } else if (eventtypes.includes(4) && e.EventType === 4) {
            const ats = ["", "H", "P", "T", ""];
            const at = ats.indexOf(e.ExtraCode1);
            if (at === -1) {
              if (attacktypes.includes(0) === false) {
                continue;
              }
            } else if (
              attacktypes.includes(ht) === false &&
              attacktypes.includes(0) === false
            )
              continue;

            if (
              attackcombos.includes(e.attackCombo) === false &&
              attackcombos.includes("All Combos") === false
            ) {
              continue;
            }

            if (
              settercalls.includes((e.settersCall ?? "").substring(0, 2)) === false &&
              settercalls.includes("All Calls") === false
            ) {
              continue;
            }

            var ht = e.SubEvent;
            if (ht > 6) ht = 6;
            if (
              hittypes.includes(ht) === false &&
              hittypes.includes(0) === false
            )
              continue;

            var bt = e.ExtraCode2 === "~" ? 5 : Number.parseInt(e.ExtraCode2);
            if (
              blocktypes.includes(bt) === false &&
              blocktypes.includes(6) === false
            )
              continue;
          }
          fes.push(e);
          mes.push(e);
        }
        s.filteredEvents = fes;
      }
    }
    onFilter(mes);
  };

  useEffect(() => {
    // filterFn (a plain (event) => boolean predicate) takes priority over the
    // react-select-shaped `filters` object below — lets a caller (e.g. the
    // MultiChoice-driven Filters page) drive filtering directly without
    // needing to shoehorn its selections into that other shape.
    if (filterFn) {
      var mes = [];
      for (var match of matches) {
        for (var ns = 0; ns < match.sets.length; ns++) {
          var s = match.sets[ns];
          var fes = s.events.filter(filterFn);
          s.filteredEvents = fes;
          mes.push(...fes);
        }
      }
      onFilter?.(mes);
    } else if (filters !== undefined && filters !== null) {
      doFilters();
    } else {
      for (var match of matches) {
        for (var ns = 0; ns < match.sets.length; ns++) {
          var s = match.sets[ns];
          s.filteredEvents = s.events;
        }
      }
    }
    // s.filteredEvents above is a mutation on the matches prop's own nested
    // objects, not React state -- force a repaint so it's reflected now
    // instead of only showing up whenever some unrelated render happens next.
    forceUpdate((n) => !n);
  }, [filters, filterFn, matches]);

  useEffect(() => {
    var cd = {};
    for (var match of matches) {
      for (var ns = 0; ns < match.sets.length; ns++) {
        var s = cd[match.guid];
        if (!s) {
          s = [false];
          for (var i = 0; i < match.sets.length; i++) {
            s.push(collapseAll);
          }
          cd[match.guid] = s;
        }
      }
      setCollapseData(cd);
    }
  }, [collapseAll]);

  useEffect(() => {}, [collapseData]);

  useEffect(() => {
    const elem = document.activeElement;
    if (elem) {
      elem?.blur();
    }
    const element = document.getElementById(selectedSet);
    if (element) {
      // 👇 Will scroll smoothly to the top of the next section
      element.scrollIntoView({
        behavior: "smooth",
        // block: "nearest",
        // inline: "start",
      });
    }
    const melement = document.getElementById(selectedMatchGuid);
    if (melement) {
      // 👇 Will scroll smoothly to the top of the next section
      melement.scrollIntoView({
        behavior: "smooth",
        // block: "nearest",
        // inline: "start",
      });
    }
    forceUpdate((n) => !n);
  }, [selectedSet, selectedMatchGuid]);

  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [activeEvent]);

  if (
    matches === undefined ||
    matches[0].sets[0].filteredEvents === undefined
  ) {
    return <></>;
  }

  const isCollapse = (match, set) => {
    if (collapseData === null) return false;
    if (!set) {
      return collapseData[match.guid][0];
    }
    return collapseData[match.guid][set.GameNumber];
  };

  const matchDesc = (match) => {
    if (team === match.teamA.Name) {
      return "(H) " + match.teamB.Name;
    } else {
      return "(A) " + match.teamA.Name;
    }
  };

  // Helper: null = unknown, true = home team, false = away team.
  const isHomeEvent = (ev) => {
    if (!ev?.Player) return null;
    if (ev.dvString !== undefined) return ev.dvString[0] === "*";
    return ev.homePlayer === true ? true : ev.homePlayer === false ? false : null;
  };

  const groupByRally = (events) => {
    const rallies = [];
    let current = null;
    for (const ev of events) {
      const key = `${ev.TeamScore ?? 0}-${ev.OppositionScore ?? 0}`;
      if (!current || current.key !== key) {
        current = {
          key,
          scoreA: ev.TeamScore ?? 0,
          scoreB: ev.OppositionScore ?? 0,
          events: [],
        };
        rallies.push(current);
      }
      current.events.push(ev);
    }
    return rallies;
  };

  // The set's running/final score. Reads `set.HomeScore`/`set.AwayScore`
  // directly (kept fresh on every live refresh by VideoAnalysis.jsx's
  // refreshLiveData, which now copies the newly-reparsed drill score onto the
  // persisted set object every poll — see its comments for why that field is
  // reliable even mid-set).
  //
  // NOTE: do NOT derive this from the last event's TeamScore/OppositionScore.
  // DVWWriter/DVWFile record a rally's score marker BEFORE the scoring event's
  // own line, then only bump + emit the incremented score marker AFTER it —
  // so the LAST event of a set carries the score as it stood BEFORE that
  // final rally (e.g. 24-15), one point behind the true result (25-15). The
  // corrected final score only lands in the drill-level HomeScore/AwayScore
  // fields via the writer's trailing corrective `*p` marker.
  const currentSetScore = (set) => {
    return { home: set.HomeScore ?? 0, away: set.AwayScore ?? 0 };
  };

  const showSetEvents = (match) => {
    return (
      <div className="space-y-1 py-1">
        {match.sets.map((set, sid) => {
          const setKey = `${match.guid}-${set.GameNumber}`;
          const ralliesCollapsed = collapsedSets.has(setKey);
          const score = currentSetScore(set);
          return (
          // ── Level 1: Set ─────────────────────────────────────────────────
          <details
            key={sid}
            open={!isCollapse(match, set)}
            className="group/set"
            id={sid + 1}
          >
            <summary className="flex items-center gap-2 px-3 py-2 bg-primary/10 border-l-4 border-l-primary cursor-pointer select-none hover:bg-primary/15 list-none [&::-webkit-details-marker]:hidden [&::marker]:hidden [&::before]:hidden">
              <ChevronRightIcon className="w-4 h-4 flex-shrink-0 text-primary transition-transform duration-150 group-open/set:rotate-90" />
              <span className="flex-1 text-sm font-bold tracking-wide">
                {t("Game").toUpperCase()} {set.GameNumber}
              </span>
              <span className="tabular-nums text-sm font-bold text-primary">
                {score.home} &ndash; {score.away}
              </span>
            </summary>

            <div className="ml-5 border-l border-l-primary/20 space-y-0.5 py-0.5">
              {groupByRally(set.filteredEvents).map((rally, rid) => {
                // ── Level 2: Rally ────────────────────────────────────────
                // Left border = serving team colour.
                const serveHome = isHomeEvent(rally.events[0]);
                const serveCls =
                  serveHome === true
                    ? "border-l-primary"
                    : serveHome === false
                    ? "border-l-secondary"
                    : "border-l-base-300";

                // Right border = winning team colour.
                const lastEv = rally.events[rally.events.length - 1];
                let winnerIsHome = null;
                if (lastEv?.DVGrade === "#") {
                  winnerIsHome = isHomeEvent(lastEv);
                } else if (lastEv?.DVGrade === "=") {
                  const loser = isHomeEvent(lastEv);
                  if (loser !== null) winnerIsHome = !loser;
                }
                const winCls =
                  winnerIsHome === true
                    ? "border-r-primary"
                    : winnerIsHome === false
                    ? "border-r-secondary"
                    : "border-r-transparent";

                return (
                  <details key={rid} open={!ralliesCollapsed} className="group/rally ml-1">
                    <summary
                      className={`flex items-center gap-2 px-2 py-1 bg-base-200 border-l-4 ${serveCls} border-r-4 ${winCls} cursor-pointer select-none hover:bg-base-300/60 list-none [&::-webkit-details-marker]:hidden [&::marker]:hidden [&::before]:hidden`}
                    >
                      <ChevronRightIcon className="w-3 h-3 flex-shrink-0 text-base-content/40 transition-transform duration-150 group-open/rally:rotate-90" />
                      <span className="tabular-nums text-xs font-semibold text-base-content/70">
                        {rally.scoreA}&thinsp;:&thinsp;{rally.scoreB}
                      </span>
                      <span className="text-xs text-base-content/35">
                        &middot;&nbsp;{rally.events.length}&nbsp;
                        {rally.events.length === 1 ? t("event") : t("events")}
                      </span>
                    </summary>

                    {/* ── Level 3: Events ─────────────────────────────── */}
                    <div className="ml-2 divide-y divide-base-200/60">
                      {rally.events.map((event, eid) => (
                        <EventItem
                          key={eid}
                          event={event}
                          isFirst={eid === 0}
                          isSelected={event === activeEvent}
                          onEventSelected={onEventSelected}
                          innerRef={event === activeEvent ? activeRowRef : undefined}
                        />
                      ))}
                    </div>
                  </details>
                );
              })}
            </div>
          </details>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-base-300/30">
      {matches.length > 1 ? (
        matches.map((match, mid) => (
          // ── Match header (multi-match only) ────────────────────────────
          <details key={mid} open={!isCollapse(match, null)} className="group/match" id={match.guid}>
            <summary className="flex items-center gap-2 px-3 py-2 bg-neutral text-neutral-content cursor-pointer select-none hover:bg-neutral/80 list-none [&::-webkit-details-marker]:hidden [&::marker]:hidden [&::before]:hidden">
              <ChevronRightIcon className="w-4 h-4 flex-shrink-0 transition-transform duration-150 group-open/match:rotate-90" />
              <span className="flex-1 text-sm font-bold">{matchDesc(match)}</span>
              <span className="text-xs opacity-70">{match.TrainingDate.toLocaleDateString()}</span>
              <span className="tabular-nums text-sm font-bold">
                {match.HomeScore} &ndash; {match.AwayScore}
              </span>
            </summary>
            {showSetEvents(match)}
          </details>
        ))
      ) : (
        showSetEvents(matches[0])
      )}
    </div>
  );
}

export default EventsList;

